import { SelectionInfo } from '../types/wordTypes';
import { DocumentReader } from './DocumentReader';
import { log, logError } from '../../utils/logging';

/* global Word, Office */

export type SelectionChangeCallback = (selection: SelectionInfo) => void;

/** Debounce live selection reads so typing does not queue a Word.run per keystroke. */
const SELECTION_DEBOUNCE_MS = 450;

export class SelectionTracker {
  private callbacks: SelectionChangeCallback[] = [];
  private isTracking = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readInFlight = false;
  private readPending = false;

  private isSelectionHandlerAvailable(): boolean {
    return (
      typeof Office !== 'undefined' &&
      !!Office.context?.document &&
      typeof Office.context.document.addHandlerAsync === 'function' &&
      !!Office.EventType?.DocumentSelectionChanged
    );
  }

  /**
   * Starts tracking selection changes
   */
  startTracking(): void {
    if (this.isTracking) {
      return;
    }

    if (!this.isSelectionHandlerAvailable()) {
      log('Selection tracking unavailable outside a Word document host');
      return;
    }

    this.isTracking = true;
    this.setupSelectionHandler();
    log('Selection tracking started');
  }

  /**
   * Stops tracking selection changes
   */
  stopTracking(): void {
    this.isTracking = false;
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.readInFlight = false;
    this.readPending = false;
    log('Selection tracking stopped');
  }

  /**
   * Registers a callback for selection changes
   */
  onSelectionChange(callback: SelectionChangeCallback): () => void {
    this.callbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Gets the current selection immediately (full read, including paragraph index).
   */
  async getCurrentSelection(): Promise<SelectionInfo> {
    return await DocumentReader.getSelectionOrParagraph();
  }

  private setupSelectionHandler(): void {
    if (!this.isSelectionHandlerAvailable()) {
      log('Selection handler setup skipped because Office document API is unavailable');
      return;
    }

    // Use Office.js event handler for selection changes
    // @ts-ignore - Office.context is available at runtime
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      this.handleSelectionChange.bind(this),
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          logError('Failed to setup selection handler', result.error);
        } else {
          log('Selection handler setup complete');
        }
      }
    );
  }

  private handleSelectionChange(): void {
    if (!this.isTracking) return;

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.processSelectionChange();
    }, SELECTION_DEBOUNCE_MS);
  }

  private async processSelectionChange(): Promise<void> {
    if (!this.isTracking) return;

    if (this.readInFlight) {
      this.readPending = true;
      return;
    }

    this.readInFlight = true;
    try {
      // Lightweight read: current selection/paragraph only — never scan the full body.
      const selection = await DocumentReader.getSelectionLight();

      this.callbacks.forEach((callback) => {
        try {
          callback(selection);
        } catch (error) {
          logError('Error in selection change callback', error);
        }
      });
    } catch (error) {
      logError('Error handling selection change', error);
    } finally {
      this.readInFlight = false;
      if (this.readPending) {
        this.readPending = false;
        void this.processSelectionChange();
      }
    }
  }
}
